// ONE TRANSFER, OPENED — Astra's "Match a payment" (chain-match; operator's ruling, 26 Oct: closer to Astra).
//
// A pending transfer opens on two panels with the same facts in the same order: "On chain" (received/sent, network,
// into, from, when, transfer) and "Suggested entry" (money in/out, party, account, when, reference). A difference in the
// amount or the coin is said in amber under the entry's figure. Astra's one boxed line, then the footer: the other
// entries on request, and ONE explicit confirm for the entry shown. Nothing is matched by itself.
//
// THE STABLE MATCH AREA (Astra's check): the candidates arrive after the sheet opens. The entry's panel is drawn whole from
// the first frame — its rows read, wait (a bar) or say nothing (—) — and the confirm sits in the footer from the start,
// disabled until a candidate is read. A late suggestion never moves a button under the pointer.
//
// Kept exactly, behind one "More actions" disclosure: Covers N postings (Tie all), Tie by books tx id (one id or several),
// the booking doors with their prefilled URLs, and Ignore's chips and Other…. Partial and split matching, no exact-amount
// rule. A look-alike or a pre-opening transfer offers only the park; a matched or ignored one, Reopen.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { api, type ChainCandidate } from '../../../lib/api';
import { ApiError, readWords } from '../../../lib/errors';
import { readState, offlineFailure } from '../../../lib/read-state';
import { fmtAmount, fmtDate, fmtDay, displayAsset, isoToDubaiWall } from '../../../lib/format';
import { partyName, railName, sameAsset, brandCase } from '../../../lib/naming';
import { Pill } from '../../kit';
import { Address, DetailSheet, Fact, Notice, Panel, Pending, ServerLine, StateLine, OfflineNotice, TAP } from '../kit';
import { CopyRef } from '../../CopyRef';
import { isPoisonRow } from '../../../lib/chain-poison';
import {
  IGNORE_REASONS, KIND_WORDS, STATUS, V3_OPEN_MS, guardShort, splitNote, tieWords, venueAccount,
  type ChainTxn, type Mark, type TieRow,
} from './rules';

/** What he presses inside the sheet: body size, 44 px on the phone (ChainActivity TOUCH). */
const TOUCH = 'text-body px-3 max-lg:min-h-11';

type Cand = ChainCandidate & { by_note?: boolean };

const walletWords = (r: TieRow) => (r.wallet_slug ? railName({ slug: r.wallet_slug }) : 'A desk wallet');
const whoOfCand = (c: { party_slug?: string | null; party_name?: string | null; party?: string | null; kind: string | null }) => {
  const slug = c.party_slug ?? c.party ?? null;
  return slug ? partyName({ display_name: c.party_name ?? null, slug }) : (KIND_WORDS[c.kind ?? ''] ?? c.kind ?? 'Entry');
};
const signed = (r: TieRow) => `${r.direction === 'in' ? '+' : '−'}${fmtAmount(r.amount, r.asset)}`;

export function ChainSheet({ r, mark, onClose }: { r: TieRow; mark: Mark; onClose: () => void }) {
  // The focus — taken on opening, kept inside, given back on closing — is the kit's DetailSheet's (27 Oct 2026).
  if (r.status !== 'pending') return <SettledSheet r={r} mark={mark} onClose={onClose} title="Transfer" />;
  if (r.lookalike_of || Date.parse(r.block_time) < V3_OPEN_MS) return <ParkSheet r={r} mark={mark} onClose={onClose} title="Transfer" />;
  if (r.custody) return <WalletOwnSheet r={r} onClose={onClose} />;
  return <MatchSheet r={r} mark={mark} onClose={onClose} title="Match a payment" />;
}

/** THE WALLET'S OWN ROW (Mechanisms, 2 Nov 2026, main 3fb99a3f): a custody sweep the wallet books itself, or a pot's send into one
 *  of its deposit addresses. The wallet books it, so nothing is offered here — no match, no Book: its words say where that
 *  booking stands, then the transfer's facts. */
function WalletOwnSheet({ r, onClose }: { r: TieRow; onClose: () => void }) {
  const c = r.custody!;
  return (
    <DetailSheet title="Transfer" onClose={onClose}>
      {/* words:ok — the wallet's own sentence about its booking, shown whole (Mechanisms sends it for this row) */}
      <Notice tone={c.state === 'refused' ? 'warn' : 'neutral'}>{c.words}</Notice>
      <div className="mt-4"><OnChain r={r} /></div>
      {c.reference && <p className="mt-3 text-meta text-tg-hint">The wallet books it as <span className="font-mono">{c.reference}</span></p>}
    </DetailSheet>
  );
}

/** The refusal of the last press on THIS row, whole (a 5xx may have partly posted: its sentence is the guard). */
function Refused({ r, mark }: { r: TieRow; mark: Mark }) {
  return mark.isError && mark.variables?.id === r.id ? <ServerLine error={mark.error} /> : null;
}

/** The matcher's guard sentences, whole and amber — what stops a second booking (ChainActivity noteParts). */
function Guards({ r }: { r: TieRow }) {
  const { guards } = splitNote(r);
  if (!guards.length && !r.lookalike_of) return null;
  return (
    <div className="mb-4 space-y-1">
      {r.lookalike_of && <p className="text-meta text-neg">Look-alike of {r.lookalike_of}&apos;s address — never pay it</p>}
      {/* A guard in one short line; the matcher's whole sentence a tap away (operator's ruling, 26 Oct). */}
      {guards.map((g) => <StateLine key={g} tone="warn" more={g}>{guardShort(g)}</StateLine>)}
    </div>
  );
}

// ── The comparison (Astra's "Match a payment": two panels, the same facts in the same order) ──────────────────────────

const Reading = () => <span className="skeleton ml-auto block w-24" aria-hidden />;
const Nothing = () => <span className="text-tg-hint">—</span>;
const Coin = ({ value, asset, tone = '' }: { value: string; asset: string; tone?: string }) => (
  <><span className={`font-mono tabular ${tone}`}>{value}</span> <span className="text-meta text-tg-hint">{displayAsset(asset)}</span></>
);

/** The transfer as the chain has it. Its rows sit where the entry's answering rows sit in the panel below. */
function OnChain({ r }: { r: TieRow }) {
  const inbound = r.direction === 'in';
  // Who, by name when the matcher knows it ("From Northern Gulf …"), else the other end's address (copied whole).
  const who = splitNote(r).lead.split(' · ').find((c) => /^(From|To) /.test(c))?.replace(/^(From|To) /, '') ?? null;
  return (
    <Panel title="On chain" flush>
      <div className="px-4 lg:px-5">
        <Fact label={inbound ? 'Received' : 'Sent'}><Coin value={fmtAmount(r.amount, r.asset)} asset={r.asset} /></Fact>
        <Fact label="Network">{r.chain}</Fact>
        <Fact label={inbound ? 'Into' : 'Out of'}>{walletWords(r)}</Fact>
        <Fact label={inbound ? 'From' : 'To'}>
          {who && r.status === 'pending' && !r.lookalike_of
            ? who
            : r.counterparty_address ? <Address value={r.counterparty_address} lookalike={!!r.lookalike_of} copy={!isPoisonRow(r)} /> : <Nothing />}
        </Fact>
        <Fact label="When">{fmtDate(r.block_time)}</Fact>
        <Fact label="Transfer"><Hash hash={r.tx_hash} /></Fact>
      </div>
    </Panel>
  );
}

/** THE SUGGESTED ENTRY, drawn whole from the first frame (the same height reading, read, empty or failed). */
function Entry({ r, c, state }: { r: TieRow; c: Cand | null; state: 'waiting' | 'failed' | 'known' }) {
  const inbound = r.direction === 'in';
  const ready = state === 'known' && !!c;
  const v = (node: ReactNode) => (ready ? node : state === 'waiting' ? <Reading /> : <Nothing />);
  const off = c ? Math.abs(c.delta) >= 0.005 : false;
  const coinDiffers = c ? !sameAsset(c.asset, r.asset) : false;
  return (
    <Panel title="Suggested entry" flush>
      <div className="px-4 lg:px-5">
        <Fact label={inbound ? 'Money in' : 'Money out'}>{v(c && (
          <>
            <Coin value={fmtAmount(c.amount, c.asset)} asset={c.asset} />
            {/* THE DIFFERENCE, visible (Astra's finding): a different coin, or the books' figure against the chain's. */}
            {coinDiffers && <span className="block text-meta text-warn">Different coin</span>}
            {!coinDiffers && off && <span className="block text-meta text-warn">{c.delta > 0 ? '+' : '−'}{fmtAmount(Math.abs(c.delta), c.asset)} against the chain</span>}
            {/* A booking partly tied to earlier sends (partial matching): what it still lacks. */}
            {c.tied > 0 && <span className="block text-meta text-info">Still lacks {fmtAmount(Math.max(0, c.remaining), c.asset)}</span>}
          </>
        ))}</Fact>
        <Fact label="Party">{v(c && whoOfCand(c))}</Fact>
        <Fact label="Account">{v(walletWords(r))}</Fact>
        <Fact label="When">{v(c && <>{fmtDate(c.timestamp)} Dubai</>)}</Fact>
        <Fact label="Reference">{v(c && (c.reference
          ? <span className="break-all font-mono">{c.reference}</span>
          : <span className="font-mono">tx {c.txn}</span>))}</Fact>
      </div>
    </Panel>
  );
}

/** Astra's boxed line under the panels, in every state. */
function MatchNotice({ c, state, error, fetching, onRetry, venue }: { c: Cand | null; state: 'waiting' | 'failed' | 'known'; error: unknown; fetching: boolean; onRetry: () => void; venue: boolean }) {
  if (state === 'waiting') return <Notice tone="neutral"><Pending inline /></Notice>;
  if (state === 'failed' && offlineFailure(error)) return <OfflineNotice what="the suggestions" />;
  if (state === 'failed') {
    return (
      <Notice tone="bad" action={<button type="button" className="btn-link text-body" disabled={fetching} onClick={onRetry}>{fetching ? 'Trying…' : 'Try again'}</button>}>
        <span title={readWords(error)}>Couldn&apos;t read the suggestions.</span>
      </Notice>
    );
  }
  if (!c) return <Notice tone="neutral">{venue ? 'No Books entry yet. Book it above.' : 'Nothing in the Books matches. Book it below.'}</Notice>;
  // A GUARD (a second booking is a double entry).
  if (c.by_note) return <Notice tone="warn">Booked from this transfer already — match it, don&apos;t book again.</Notice>;
  return <Notice tone="neutral">Suggested match. Review both records before confirming.</Notice>;
}

/** The chain's hash: short, the whole of it a tap away (and copied whole). */
function Hash({ hash }: { hash: string }) {
  const [shown, setShown] = useState(false);
  return shown
    ? <span className="block break-all font-mono text-meta"><CopyRef value={hash} label={hash} className="whitespace-normal" /></span>
    : <button type="button" className="btn-link font-mono text-body max-lg:min-h-11" aria-label="Show the whole hash" onClick={() => setShown(true)}>{hash.slice(0, 10)}…</button>;
}

/** The other candidates, on request: each says what it is and can be put in the comparison. */
function Others({ list, selected, onPick }: { list: Cand[]; selected: number | null; onPick: (txn: number) => void }) {
  return (
    <div className="space-y-2">
      {list.filter((c) => c.txn !== selected).map((c) => {
        const off = Math.abs(c.delta) >= 0.005;
        return (
          <div key={c.txn} className="flex items-center gap-3 rounded-lg border border-stroke px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-mono tabular text-body">{fmtAmount(c.amount, c.asset)}</span>
                <span className="text-meta text-tg-hint">{displayAsset(c.asset)}</span>
                {off && <span className="text-meta text-warn tabular">books {c.delta > 0 ? '+' : '−'}{fmtAmount(Math.abs(c.delta), c.asset)} vs chain</span>}
                {c.by_note && <span className="text-meta text-pos">booked from this row</span>}
                {c.tied > 0 && <span className="text-meta text-info">{c.tied} tied · lacks {fmtAmount(Math.max(0, c.remaining), c.asset)}</span>}
              </div>
              <div className="break-words text-meta text-tg-hint">
                {whoOfCand(c)} · {fmtDate(c.timestamp)}
                {c.reference && <> · <span className="whitespace-nowrap font-mono">{c.reference}</span></>}
                {' · '}<span className="whitespace-nowrap font-mono">tx {c.txn}</span>
              </div>
            </div>
            <button type="button" className={`btn-secondary shrink-0 ${TOUCH}`} onClick={() => onPick(c.txn)}>Compare</button>
          </div>
        );
      })}
    </div>
  );
}

// THE MOVE IN TERMINOLOGY'S WORDS (docs/TERMINOLOGY.md §2): "1,204,000 USDT · Hot · TRC20 → Bitget Main" — the coin without its
// chain (the wallet says the network), the network spelled TRC20 · ERC20 · BEP20, the venue account by its Books name. Until the
// register's one walletName() lands (N-091), the desk wallet's name is railName's with its network respelled.
const NETWORK: Record<string, string> = { TRC: 'TRC20', TRON: 'TRC20', ERC: 'ERC20', ETH: 'ERC20', BEP: 'BEP20', BSC: 'BEP20' };
const coinWord = (asset: string) => displayAsset(asset).split('·')[0]!;
const deskWords = (r: TieRow) => walletWords(r).replace(/ · (TRC|TRON|ERC|ETH|BEP|BSC)$/, (_, n: string) => ` · ${NETWORK[n]}`);

/** THE MOVE, FIRST (restored 30 Oct 2026; his words: "it was clearly visible in earlier books version"). The old Chain screen
 *  opened a venue movement on "Move to Bitget →"; batch 2 (0e5d9256) put it under More actions and the panel said "No
 *  suggestion". A desk send onto a venue, or a venue's withdrawal onto a desk wallet, is a move between the desk's own
 *  accounts: what moved, from where to where, and ONE Book button — the server books the move it pairs with this send and
 *  ties the row in one go (POST /api/chain/:id/book-move): two taps from the row, where it was five. Its refusals are its own
 *  words; "still pending at the venue" offers Book anyway. Move money, prefilled, stays one link away for a move to adjust. */
function VenueMove({ r, onBooked }: { r: TieRow; onBooked: (ref: string) => void }) {
  const qc = useQueryClient();
  const book = useMutation({
    mutationFn: (confirmPending: boolean) =>
      api.chainBookMove(r.id, { venue_movement_id: r.venue_movement_id!, ...(confirmPending ? { confirm_pending: true } : {}) }),
    // Booked and tied: the row leaves Unmatched, the sheet closes. Booked, not tied yet (tied:false): the posting stands and the next
    // chain read ties it by its note — said here, with its reference, and nothing to press again.
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ['chain'] }); if (d.tied) onBooked(d.ref); },
  });
  const url = venueMoveUrl(r);
  if (!url || !r.venue_slug) return null;
  const [from, to] = r.direction === 'in' ? [venueAccount(r), deskWords(r)] : [deskWords(r), venueAccount(r)];
  const e = book.error instanceof ApiError ? book.error : null;
  const pendingAtVenue = e?.status === 409 && e.code === 'venue_pending';
  const refused = book.error ? (e?.status === 409 && e.body.reason ? e.body.reason : readWords(book.error)) : null;
  return (
    <Panel title="Suggested move" className="mb-4" flush>
      <div className="space-y-3 px-4 py-3 lg:px-5">
        <p className="text-body">
          <span className="whitespace-nowrap font-mono tabular">{fmtAmount(r.amount, r.asset)}&nbsp;{coinWord(r.asset)}</span>
          {' · '}{from} → {to}
        </p>
        {refused && <p className={`text-meta ${pendingAtVenue ? 'text-warn' : 'text-neg'}`}>{refused}</p>}
        {book.data && !book.data.tied && (
          <p className="text-meta text-tg-hint">Booked {book.data.ref}. {book.data.reason ?? 'It ties on the next chain read.'}</p>
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {r.venue_movement_id != null && !book.data && (
            <button type="button" className={`btn-primary ${TOUCH}`} disabled={book.isPending}
              onClick={() => book.mutate(pendingAtVenue)}>
              {book.isPending ? 'Booking…' : pendingAtVenue ? 'Book anyway' : 'Book move'}
            </button>
          )}
          <Link className={`${TAP} text-body`} to={url}>{r.venue_movement_id != null ? 'Adjust in Move money →' : 'Book move →'}</Link>
        </div>
      </div>
    </Panel>
  );
}

/** WHO SENT IT, FROM THE BOOKS' OWN TIES (30 Oct 2026, his "multiple clicks for chain entry"): when no Books entry is near and the
 *  sender's address belongs to ONE party, the sheet leads with that party and two doors — Book as trade (customer sells on a
 *  receipt, buys on a send) and Money in/out — each with everything filled, one confirm on the form. */
function SuggestedTrade({ r, party }: { r: TieRow; party: { slug: string; display_name: string; why: string } }) {
  const inbound = r.direction === 'in';
  const name = partyName({ slug: party.slug, display_name: party.display_name });
  return (
    <Panel title="Suggested party" className="mb-4" flush>
      <div className="space-y-3 px-4 py-3 lg:px-5">
        <p className="text-body"><span className="font-medium">{name}</span> <span className="text-meta text-tg-hint">· {party.why}</span></p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Link className={`btn-primary ${TOUCH}`} to={tradeUrl(r, party.slug)}>Book as trade</Link>
          <Link className={`${TAP} text-body`} to={recordUrl(r, party.slug)}>{inbound ? 'Money in' : 'Money out'} →</Link>
        </div>
      </div>
    </Panel>
  );
}

function MatchSheet({ r, mark, onClose, title }: { r: TieRow; mark: Mark; onClose: () => void; title: ReactNode }) {
  const q = useQuery({ queryKey: ['chain', 'candidates', r.id], queryFn: () => api.chainCandidates(r.id), staleTime: 0 });
  const state = readState(q);
  const list = (state === 'known' ? q.data?.candidates ?? [] : []) as Cand[];
  const [picked, setPicked] = useState<number | null>(null);
  const [others, setOthers] = useState(false);
  const othersRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (others) othersRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, [others]);
  // The suggestion: the one he picked, else the entry booked FROM this row (the guard's own), else the nearest.
  const c = list.find((x) => x.txn === picked) ?? list.find((x) => x.by_note) ?? list[0] ?? null;
  const party = state === 'known' ? q.data?.suggested_party ?? null : null;
  /** The read answered and there is nothing to match and nothing suggested: the booking doors go on the sheet itself. */
  const bare = state === 'known' && !c && !venueMoveUrl(r) && !party;
  const more = list.length - (c ? 1 : 0);
  return (
    <DetailSheet
      wide
      title={title}
      onClose={onClose}
      footer={
        <div className="space-y-2">
          <Refused r={r} mark={mark} />
          {/* Astra's pair: the others on request, and THE ONE CONFIRM for the entry shown — two fixed cells, so the confirm
              is there from the first frame and never moves when the suggestions land. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              {more > 0 && (
                <button type="button" className="btn-secondary w-full px-3 text-body" aria-expanded={others} onClick={() => setOthers((x) => !x)}>
                  {others ? 'Hide others' : 'Other entries'}
                </button>
              )}
            </div>
            {state === 'known' && !c
              ? <span />
              : (
                <button type="button" className="btn-primary w-full px-3 text-body font-medium"
                  disabled={!c || mark.isPending}
                  onClick={() => c && mark.mutate({ id: r.id, action: 'match', ledger_txn_id: c.txn })}>
                  Confirm match
                </button>
              )}
          </div>
        </div>
      }
    >
      <Guards r={r} />
      <VenueMove r={r} onBooked={() => onClose()} />
      {party && !c && !venueMoveUrl(r) && <SuggestedTrade r={r} party={party} />}
      {/* Side by side from lg (the wide sheet), stacked on the phone — the same facts in the same order (Astra). */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
          <OnChain r={r} />
          <Entry r={r} c={c} state={state} />
        </div>
        <MatchNotice c={c} state={state} error={q.error} fetching={q.isFetching} onRetry={() => q.refetch()} venue={!!venueMoveUrl(r) || party !== null} />
      </div>
      {/* NOTHING TO MATCH, NOTHING SUGGESTED (his "if there are no suggestion, why 1 more click", 1 Nov 2026): booking it is all
          that is left, so its doors are on the sheet — not one tap away under More actions. */}
      {bare && (
        <Panel title="Book it" className="mt-4" flush>
          <div className="px-4 py-3 lg:px-5"><Doors r={r} party={null} /></div>
        </Panel>
      )}
      {others && more > 0 && <div ref={othersRef} className="mb-4"><Others list={list} selected={c?.txn ?? null} onPick={(t) => { setPicked(t); setOthers(false); }} /></div>}

      {/* Every other way to settle this transfer, kept exactly, behind one tap (Astra: the comparison and one confirm). */}
      <details className="text-body">
        <summary className="flex min-h-11 cursor-pointer select-none items-center text-tg-link lg:min-h-10">More actions</summary>
        <div className="mt-3 space-y-5">
          <CoversTie r={r} mark={mark} />
          <TieById r={r} mark={mark} />
          {!bare && <Doors r={r} party={party?.slug ?? null} />}
          <IgnoreChips r={r} mark={mark} only={['Dust / spam token', "Not the desk's move"]} />
        </div>
      </details>
    </DetailSheet>
  );
}

/** A look-alike, or a transfer dated before the 1 September opening: nothing to book or tie — park it (ChainActivity). */
function ParkSheet({ r, mark, onClose, title }: { r: TieRow; mark: Mark; onClose: () => void; title: ReactNode }) {
  return (
    <DetailSheet title={title} onClose={onClose} footer={mark.isError && mark.variables?.id === r.id ? <Refused r={r} mark={mark} /> : undefined}>
      <Guards r={r} />
      <OnChain r={r} />
      <div className="mt-6 space-y-3">
        {r.lookalike_of
          ? <>
              <p className="text-meta text-tg-hint">Nothing here to book or tie — park it:</p>
              <IgnoreChips r={r} mark={mark} only={['Dust / spam token', "Not the desk's move"]} />
            </>
          : <>
              <p className="text-meta text-tg-hint">Dated {fmtDay(r.block_time)} — before these books opened on 1 September. Park it:</p>
              <IgnoreChips r={r} mark={mark} only={['Pre-cutover']} />
            </>}
      </div>
    </DetailSheet>
  );
}

/** A matched or ignored transfer: its facts, what it is tied to (both sides when they disagree), and Reopen. */
function SettledSheet({ r, mark, onClose, title }: { r: TieRow; mark: Mark; onClose: () => void; title: ReactNode }) {
  const words = tieWords(r);
  const status = STATUS[r.status] ?? { word: r.status, tone: 'neutral' as const };
  const txns = r.ledger_txn_id != null ? [r.ledger_txn_id] : (r.tie_txns ?? []);
  return (
    <DetailSheet
      title={title}
      onClose={onClose}
      footer={
        <div className="space-y-2">
          <Refused r={r} mark={mark} />
          <button type="button" className={`btn-secondary w-full ${TOUCH}`} disabled={mark.isPending}
            onClick={() => mark.mutate({ id: r.id, action: 'reopen' })}>
            Reopen as pending
          </button>
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Pill tone={status.tone}>{status.word}</Pill>
        {r.note && <span className="text-meta text-tg-hint">{r.note}</span>}
      </div>
      <OnChain r={r} />
      {txns.length > 0 && (
        <Panel title="Tied entry" aside={txns.length > 1 ? `${txns.length} postings` : undefined} flush className="mt-4">
          <div className="px-4 lg:px-5">
            {txns.map((t, i) => (
              <Fact key={t} label={`tx ${t}`}>
                {r.tie_amounts?.[i] != null ? <Coin value={fmtAmount(r.tie_amounts[i]!, r.asset)} asset={r.asset} /> : <Nothing />}
              </Fact>
            ))}
          </div>
        </Panel>
      )}
      {/* MORE TIED THAN THE ENTRY HOLDS (over_tied, 4 Nov 2026): amber, information only. */}
      {r.over_tied && <p className="mt-3 text-meta text-warn" data-over-tied="">{r.over_tied}</p>}
      {/* THE TWO SIDES OF THE TIE, in words, when they disagree by more than dust; within dust the residue is still shown. */}
      {words
        ? <p className="mt-3 text-meta text-warn">{words}</p>
        : r.confidence === 'rounded' && r.delta != null && <p className="mt-3 text-meta text-tg-hint">rounded Δ{r.delta.toFixed(6)}</p>}
    </DetailSheet>
  );
}

// ── The doors, the sets, the typed id and Ignore — ChainActivity's, unchanged in what they open and post ────────────────

/** THE VENUE ON THE FAR END (16 Sept 2026): Move money with both accounts, the chain's figure and time, the fee and note —
 *  null when the row is not a venue movement (r.venue, bff/src/routes/chain.ts venueLinkSql). */
export function venueMoveUrl(r: TieRow): string | null {
  const inbound = r.direction === 'in';
  if (!r.venue || !r.venue_slug) return null;
  const q = new URLSearchParams({
    rail: inbound ? r.venue_slug : r.wallet_slug, railAsset: r.asset,
    to:   inbound ? r.wallet_slug : r.venue_slug, toAsset: r.asset,
    amount: String(r.amount),
    when: isoToDubaiWall(r.block_time),
    note: 'chain ' + r.tx_hash.slice(0, 14),
  });
  if (inbound && r.venue_fee && r.venue_fee > 0 && sameAsset(r.venue_fee_asset, r.asset)) q.set('fee', String(r.venue_fee));
  return `/treasury/rebalance?${q.toString()}`;
}

/** BOOK IT AS A TRADE, or as money in/out: the chain's amount, coin, wallet and time, the transfer's note (the hash the matcher
 *  ties by), and the suggested party when the Books' ties give one (his "multiple clicks for chain entry", 30 Oct 2026). */
export function tradeUrl(r: TieRow, party: string | null): string {
  const q = new URLSearchParams({ dir: r.direction === 'in' ? 'customer_sell' : 'customer_buy', [r.direction === 'in' ? 'take' : 'give']: String(r.amount),
    rail: r.wallet_slug, railAsset: r.asset, lock: 'coin', when: isoToDubaiWall(r.block_time), note: 'chain ' + r.tx_hash.slice(0, 14) });
  if (party) q.set('party', party);
  return `/trade?${q.toString()}`;
}
export function recordUrl(r: TieRow, party: string | null): string {
  const q = new URLSearchParams({ dir: r.direction === 'in' ? 'in' : 'out', amount: String(r.amount), asset: r.asset, rail: r.wallet_slug, railAsset: r.asset,
    when: isoToDubaiWall(r.block_time), note: 'chain ' + r.tx_hash.slice(0, 14) });
  if (party) q.set('party', party);
  return `/record?${q.toString()}`;
}

/** BOOK IT FROM HERE: wallet, asset, amount and the chain's own time prefilled; &lock=coin; the form's own words. */
function Doors({ r, party }: { r: TieRow; party: string | null }) {
  const inbound = r.direction === 'in';
  const chainNote = encodeURIComponent('chain ' + r.tx_hash.slice(0, 14));
  const when = encodeURIComponent(isoToDubaiWall(r.block_time));
  const moveUrl = venueMoveUrl(r);
  const door = `btn-secondary ${TOUCH}`;
  return (
    <div className="flex flex-wrap gap-2">
      {moveUrl && r.venue && (
        <Link className={door} to={moveUrl}>{inbound ? `Move from ${brandCase(r.venue)} →` : `Move to ${brandCase(r.venue)} →`}</Link>
      )}
      <Link
        className={door}
        to={tradeUrl(r, party)}
      >
        {inbound ? 'Book trade · Customer sells' : 'Book trade · Customer buys'}
      </Link>
      <Link
        className={door}
        title={inbound ? 'A receipt, or a move between your own accounts' : 'A payment, or a move between your own accounts'}
        to={recordUrl(r, party)}
      >
        {inbound ? 'Money in' : 'Money out'}
      </Link>
      {inbound && (
        <Link className={door} title="A customer's deposit on a cheque"
          to={`/mc?rail=${r.wallet_slug}&railAsset=${r.asset}&take=${r.amount}&lock=coin&note=${chainNote}`}>
          Cheque deposit
        </Link>
      )}
      {inbound && (
        <Link className={door} title="A delivery charge paid in coin"
          to={`/record?mode=charge&amount=${r.amount}&rail=${r.wallet_slug}&railAsset=${r.asset}&when=${when}&note=${chainNote}`}>
          Delivery charge
        </Link>
      )}
    </div>
  );
}

/** ONE SEND, SEVERAL POSTINGS — the matcher's own set, one tap (db/0061). The figures are the route's. */
function CoversTie({ r, mark }: { r: TieRow; mark: Mark }) {
  const sets = r.covers ?? [];
  if (!sets.length || r.status !== 'pending') return null;
  return (
    <div className="space-y-2">
      {sets.map((c, i) => (
        <div key={i} className="flex items-center gap-3 rounded-lg border border-stroke px-3 py-2">
          <div className="min-w-0 flex-1 text-meta">
            <span className="text-tg-text">Covers {c.txns.length} postings together</span>
            <span className="text-tg-hint">
              {' · '}{c.txns.map((t, j) => `tx ${t}${c.refs[j] ? ' ' + c.refs[j] : ''}${c.parties[j] ? ' (' + c.parties[j] + ')' : ''}`).join(' + ')}
              {' = '}{fmtAmount(Number(c.total), r.asset)} {displayAsset(r.asset)}
            </span>
          </div>
          <button type="button" className={`btn-secondary shrink-0 ${TOUCH}`} disabled={mark.isPending}
            onClick={() => mark.mutate({ id: r.id, action: 'match', ties: c.txns.map((t) => ({ ledger_txn_id: t })) })}>
            Tie all {c.txns.length}
          </button>
        </div>
      ))}
    </div>
  );
}

/** TIE BY BOOKS TX ID — one id or several; each resolved before the button exists; the button under the lines it acts
 *  on, in the posting's own words (ChainActivity TieById). Reads through api.chainTxn (the same GET /api/chain/txn/:txn). */
function TieById({ r, mark }: { r: TieRow; mark: Mark }) {
  const [txn, setTxn] = useState('');
  const [settled, setSettled] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSettled(txn.trim()), 350);
    return () => clearTimeout(t);
  }, [txn]);
  const ids = [...new Set(settled.split(/[\s,+]+/).filter(Boolean))];
  const valid = ids.length > 0 && ids.every((s) => /^\d+$/.test(s) && Number(s) > 0);
  const nums = valid ? ids.map(Number) : [];
  const looks = useQueries({
    queries: nums.map((id) => ({
      queryKey: ['chain', 'txn', id],
      queryFn: () => api.chainTxn(id) as Promise<ChainTxn>,
      retry: false,
      staleTime: 60_000,
    })),
  });
  const found = looks.map((q, i) => (q.data && q.data.txn === nums[i] ? q.data : null));
  const fitsOne = (t: ChainTxn) => t.wallet_slug === r.wallet_slug && t.direction === r.direction && !!t.asset && sameAsset(t.asset, r.asset);
  const allFit = valid && found.length > 0 && found.every((t) => !!t && fitsOne(t));
  const fetching = looks.some((q) => q.isFetching);
  const submit = () => (nums.length === 1
    ? mark.mutate({ id: r.id, action: 'match', ledger_txn_id: nums[0]! })
    : mark.mutate({ id: r.id, action: 'match', ties: nums.map((n) => ({ ledger_txn_id: n })) }));
  const tieWordsFor = nums.length === 1
    ? `Tie to tx ${nums[0]}${found[0] ? ` — ${whoOfCand(found[0])}` : ''}`
    : `Tie to these ${nums.length} — ${nums.map((n) => `tx ${n}`).join(' + ')}`;
  return (
    <details className="text-meta text-tg-hint">
      <summary className="cursor-pointer select-none text-body max-lg:py-3">Tie by books tx id</summary>
      <input className="input mt-2 w-full text-field lg:text-body" placeholder="482 — or 4944, 4945"
        value={txn} onChange={(e) => setTxn(e.target.value)} inputMode="numeric" />
      {valid && fetching && <p className="mt-1"><Pending inline /></p>}
      {nums.map((id, i) => {
        const look = looks[i]!;
        const t = found[i];
        if (look.isError) {
          return (
            // words:ok — a lookup's refusal, shown whole beside the id it is about
            <p key={id} className="mt-1 text-neg">
              {look.error instanceof ApiError && look.error.status === 404
                ? `No live books transaction ${id} — check the id.`
                : `Couldn't read tx ${id} — ${readWords(look.error)}`}
            </p>
          );
        }
        if (!t) return null;
        const fits = fitsOne(t);
        return (
          <p key={id} className={`mt-1 ${fits ? 'text-tg-text' : 'text-warn'}`}>
            <span className="font-mono">tx {t.txn}</span> · {whoOfCand(t)}
            {t.amount != null && t.asset && <> · {fmtAmount(t.amount, t.asset)} {displayAsset(t.asset)}</>}
            {t.timestamp && <>{' · '}{fmtDay(t.timestamp)}</>}
            {t.reference && <> · <span className="font-mono">{t.reference}</span></>}
            {!fits && (
              <> — {t.wallet_slug
                ? `moves ${t.asset ? displayAsset(t.asset) : 'coins'} ${t.direction === 'in' ? 'into' : 'out of'} ${railName({ slug: t.wallet_slug })}; this row is ${displayAsset(r.asset)} ${r.direction === 'in' ? 'into' : 'out of'} ${r.wallet_slug ? railName({ slug: r.wallet_slug }) : 'a desk wallet'}`
                : 'touches no desk wallet'}. Not this one.</>
            )}
          </p>
        );
      })}
      {allFit && (
        <button type="button" className={`btn-secondary mt-2 w-full ${TOUCH}`}
          disabled={mark.isPending || txn.trim() !== settled}
          onClick={submit}>
          {tieWordsFor}
        </button>
      )}
    </details>
  );
}

/** IGNORE: one tap on a preset chip, or "Other…" for the box; `only` narrows the chips (ChainActivity IgnoreChips). */
function IgnoreChips({ r, mark, only }: { r: TieRow; mark: Mark; only?: string[] }) {
  const [other, setOther] = useState(false);
  const [note, setNote] = useState('');
  const chips = only ? IGNORE_REASONS.filter((c) => only.includes(c.label)) : IGNORE_REASONS;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-meta text-tg-hint">Ignore —</span>
        {chips.map((c) => (
          <button key={c.label} type="button" className={`btn-secondary ${TOUCH}`}
            disabled={mark.isPending} title={c.note}
            onClick={() => mark.mutate({ id: r.id, action: 'ignore', note: c.note })}>
            {c.label}
          </button>
        ))}
        <button type="button" className={`btn-secondary ${TOUCH}${other ? ' bg-panel-2' : ''}`} aria-expanded={other}
          disabled={mark.isPending} onClick={() => setOther((v) => !v)}>
          Other…
        </button>
      </div>
      {other && (
        <div className="flex gap-2">
          <input className="input min-w-0 flex-1 text-field lg:text-body" placeholder="Anything to remember"
            value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="button" className={`btn-secondary ${TOUCH}`}
            disabled={note.trim().length < 4 || mark.isPending}
            onClick={() => mark.mutate({ id: r.id, action: 'ignore', note: note.trim() })}>
            Ignore
          </button>
        </div>
      )}
    </div>
  );
}
